import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { BASE_URL } from '@/constants';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:bg-disabled disabled:border-border disabled:text-disabled [&_svg]:pointer-events-none [&_svg]:size-[16px] [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'border bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        primary:
          'bg-primary text-primary-foreground shadow hover:bg-primary/80 disabled:bg-disable',
        outline: 'border border-primary text-primary hover:border-primary/70 hover:text-primary/70',
        ghost: 'hover:bg-hover hover:text-accent-foreground',
        link: 'text-primary hover:text-primary/70',
        // destructive:
        //   "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        // secondary:
        //   "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
      },
      size: {
        default: 'px-[12px] py-[6px]',
        sm: 'px-[8px] py-[2px] gap-sm text-sm',
        icon: 'p-[4px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  icon?: React.ReactNode;
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, icon, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={loading}
        {...props}
      >
        {loading ? <img src={`${BASE_URL}/loading.svg`} className="size-[14px]" /> : icon}
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
